import { getCsrfToken } from '../../utils/auth';

// BOM module settings — loaded from `/api/bom/settings` when the module mounts, then read
// synchronously by the tree code (which was written against a synchronous store).
//
// In the standalone app these came from `public/settings.xml`, one file per deployment,
// which is why it could only ever serve one tenant. Here they are a row per TENANT on the
// server: everyone reads them, only an Orcanos administrator writes them (Admin → Settings).
//
// ⚠️ `getSettings()` is module-level state. `loadSettings()` MUST have resolved before the
// tree mounts — BomViewer renders nothing below its header until it has — or the tree reads
// the empty defaults and fires a filter call with no filter id.

const EMPTY = {
  baseUrl: '',
  versionId: '',
  bomFilterId: '',
  instanceFilterId: '',
  partCatalogFilterId: '',
  relatedEcoFilterId: '',
  pageSize: 50,
  bomNamePrefixes: [],
  imageUrlQuery: '',
  usePartInstance: true,
  usePartHierarchy: false,
  ecoVersionId: '',
  dmsFilterId: '',
  dmsVersionId: '',
  ecoFilterId: '',
  ecoListVersionId: '',
  ecoLabel: '',
  // Assembly structure: two work item types, one per depth, parts as the leaves.
  useAssembly: false,
  assemblyVersionId: '',
  assemblyRootFilterId: '',
  assemblyRootType: '',
  assemblyFilterId: '',
  assemblyType: '',
  assemblyRootLabel: '',
  assemblyLabel: '',
};

let _settings = { ...EMPTY };
let _user = '';

// Fired on window whenever settings are adopted, so the left nav (which lives outside the
// module) can add or drop the DMS / ECO entries right after an admin saves.
export const SETTINGS_EVENT = 'bom-settings-changed';

/** Adopt a server response ({ configured, can_edit, settings }) as the current settings. */
export function applySettings(payload) {
  _settings = { ...EMPTY, ...(payload?.settings || {}) };
  if (_settings.baseUrl && !_settings.baseUrl.endsWith('/')) _settings.baseUrl += '/';
  try { window.dispatchEvent(new CustomEvent(SETTINGS_EVENT, { detail: _settings })); } catch { /* no window (tests) */ }
  return _settings;
}

/**
 * The module's views, in nav order. BOMs and Part Catalog are always listed (they render
 * their own "not configured" state); DMS items and the ECO list appear only once their
 * filter is set. The ECO entry is named after the CR work item in its project.
 */
export const PS_VIEWS = [
  { key: 'ps-dashboard', label: 'Dashboard' },
  { key: 'ps-structures', label: 'Product structures' },
  { key: 'ps-import', label: 'Import BOM file' },
  { key: 'ps-parts', label: 'Part usage' },
  { key: 'ps-baselines', label: 'Baselines' },
  { key: 'ps-branches', label: 'Branches' },
];

const psView = (key) => PS_VIEWS.find((v) => v.key === key);

export function bomViewsFor(settings) {
  const s = settings || {};
  // Product structures (2026-10-28): the hierarchy kept in the BOM Viewer — its own switch,
  // beside the Orcanos views, never instead of them. Routes are static `/bom/ps-*`. Nav order
  // (user, 2026-10-29): Dashboard, Product structures, the Orcanos views, then Part usage,
  // Baselines, Branches. Menu slimmed (user, 2026-10-30): Import BOM file is only the button on
  // Product structures, and BOMs / Assemblies (the Orcanos structures' tree) leave the menu
  // while Product structures is on — their routes still open from a bookmark or link. Baselines
  // and Branches left the menu too (user, 2026-10-30): buttons on Product structures open them
  // for the selected product. The Assembly list came back (user, 2026-11-02): it is the
  // Orcanos list of the Assembly work items, still wanted beside Product structures; the
  // settings tick `psShowAssemblies` (default on) hides it.
  const ps = !!s.useViewerHierarchy;
  const views = ps ? [psView('ps-dashboard'), psView('ps-structures')] : [];
  // Under the assembly structure each work item is its own expandable list, named after
  // the work item the admin picked: the BOM type at the top, the Assembly type beside it.
  // Both expand the same way (sub-assemblies, then parts) — "Assemblies" is a different
  // STARTING POINT, not a different tree.
  if (!ps) views.push({ key: 'boms', label: (s.useAssembly && s.assemblyRootLabel) || 'BOMs' });
  // Beside Product structures the list is offered under ANY BOM structure (user, 2026-11-02);
  // mirrors `bom_api._assembly_list_on`.
  if (s.assemblyFilterId && (ps ? s.psShowAssemblies !== false : s.useAssembly)) {
    views.push({ key: 'assemblies', label: s.assemblyLabel || 'Assemblies' });
  }
  views.push({ key: 'parts', label: 'Part Catalog' });
  if (s.dmsFilterId) views.push({ key: 'dms', label: 'Parts - DMS items' });
  if (s.ecoFilterId) views.push({ key: 'ecos', label: s.ecoLabel || 'ECOs' });
  if (ps) views.push(psView('ps-parts'));
  return views;
}

/** The project version an item of this type is read from — and its Orcanos link points at. */
export function versionForType(type, settings = _settings) {
  if (type === 'DMS') return settings.dmsVersionId || settings.versionId;
  if (type === 'CR') return settings.ecoListVersionId || settings.ecoVersionId || settings.versionId;
  // The assembly types may live in their own project — an assembly row's Orcanos link has
  // to point there, not at the parts project, or it opens the wrong item or none at all.
  // PRT is deliberately NOT included: parts stay in the parts project even when the
  // assemblies above them do not.
  const asm = [settings.assemblyRootType, settings.assemblyType]
    .filter(Boolean).map((t) => String(t).toUpperCase());
  if (type && asm.includes(String(type).toUpperCase())) {
    return settings.assemblyVersionId || settings.versionId;
  }
  return settings.versionId;
}

/** Which of the three BOM structures this tenant uses. Mirrors `bom_api._structure` —
 *  the precedence has to be identical on both sides or the tree expands through a filter
 *  the server is not reading from. */
export function bomStructure(settings = _settings) {
  if (settings.useAssembly) return 'assembly';
  if (settings.usePartHierarchy) return 'hierarchy';
  if (settings.usePartInstance === false) return 'none';
  return 'instance';
}

/**
 * Fetch this tenant's settings. Resolves to the full server payload so the caller can
 * read `configured` and `can_edit`; throws with the server's message on failure.
 */
export async function loadSettings() {
  const resp = await fetch('/api/bom/settings', { credentials: 'same-origin' });
  let data = null;
  try { data = await resp.json(); } catch { /* handled below */ }
  if (!resp.ok) {
    const err = new Error((data && data.detail) || `Could not load BOM settings (HTTP ${resp.status}).`);
    err.httpCode = resp.status;
    throw err;
  }
  applySettings(data);
  return data;
}

/** Save (admin only — the server 403s anyone else). Resolves to the new server payload. */
export async function saveSettings(values) {
  const resp = await fetch('/api/bom/settings', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': getCsrfToken() },
    body: JSON.stringify(values),
  });
  let data = null;
  try { data = await resp.json(); } catch { /* handled below */ }
  if (!resp.ok) {
    const detail = data && (typeof data.detail === 'string' ? data.detail : null);
    throw new Error(detail || `Could not save BOM settings (HTTP ${resp.status}).`);
  }
  applySettings(data);
  return data;
}

export function getSettings() {
  return _settings;
}

/**
 * Who is signed in — keys the per-user preferences the tree keeps in localStorage (visible
 * columns, search history, export selection). Was the `covaris_user` localStorage entry the
 * standalone login wrote; here it is set from the platform session by BomViewer.
 */
export function setBomUser(userId) {
  _user = String(userId || '');
}

export function getBomUser() {
  return _user;
}
